import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { PageHeader, DateRangePicker } from '../components/common';
import { ROUTES } from '../constants';
import { reversalService, ApiError } from '../services';
import moment from 'moment';

const buildParamsFromSearchParamsReversal = (params, paginationSize = 20) => {
  const requestParams = {
    page: parseInt(params.page, 10) || 1,
    size: parseInt(params.size, 10) || paginationSize,
    channel: params.channel || 'ALL',
  };
  if (params.keyword && params.keyword.length >= 3) requestParams.keyword = params.keyword;
  if (params.dateFrom && params.dateTo) {
    requestParams.dateFrom = params.dateFrom;
    requestParams.dateTo = params.dateTo;
  }
  if (params.channel) requestParams.channel = params.channel;
  return requestParams;
};

const serializeFiltersToParamsReversal = (state) => {
  const p = {};
  if (state.page && state.page !== 1) p.page = state.page;
  if (state.size && state.size !== 20) p.size = state.size;
  if (state.keyword && state.keyword.length >= 3) p.keyword = state.keyword;
  if (state.dateFrom) p.dateFrom = state.dateFrom;
  if (state.dateTo) p.dateTo = state.dateTo;
  if (state.channel && state.channel !== 'ALL') p.channel = state.channel;
  return p;
};

const ReversalTransactionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasInitialLoadRef = useRef(false);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [channelFilter, setChannelFilter] = useState('');

  // State for API data
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });

  // Fetch transactions from API
  const fetchTransactions = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const requestParams = {
        page: params.page || 1,
        size: params.size || 20,
        channel: params.channel || 'ALL',
      };

      // Add keyword if search term is at least 3 chars
      if (params.keyword && params.keyword.length >= 3) {
        requestParams.keyword = params.keyword;
      }

      // Add date range if provided
      if (params.dateFrom && params.dateTo) {
        requestParams.dateFrom = params.dateFrom;
        requestParams.dateTo = params.dateTo;
      }

      // Add channel if provided
      if (params.channel) {
        requestParams.channel = params.channel;
      }

      const response = await reversalService.getFormattedTransactionList(
        requestParams
      );

      if (response && response.data) {
        setTransactions(response.data.data || []);
        setPagination({
          page: response.data.page || 1,
          pageCount: response.data.pageCount || 1,
          size: response.data.size || 20,
          count: response.data.count || 0,
          total: response.data.total || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch snap earn transactions:', err);

      if (err instanceof ApiError) {
        setError(`Failed to load transactions: ${err.message}`);
      } else {
        setError('Failed to load transactions. Please try again.');
      }

      setTransactions([]);
      setPagination(prev => ({ ...prev, count: 0, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch: read filter from URL so filter persists after cancel/reverse from detail
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      const params = Object.fromEntries(searchParams.entries());
      setSearchTerm(params.keyword || '');
      setStartDate(params.dateFrom || '');
      setEndDate(params.dateTo || '');
      setChannelFilter(params.channel || '');
      const page = parseInt(params.page, 10) || 1;
      const size = parseInt(params.size, 10) || 20;
      setPagination(prev => ({ ...prev, page, size }));
      const requestParams = buildParamsFromSearchParamsReversal(params, size);
      fetchTransactions(requestParams);
    }
  }, [fetchTransactions, searchParams]);

  // Handle search input
  const handleSearchFocus = useCallback(() => {
    setSearchFocused(true);
  }, []);

  const handleSearchChange = useCallback(e => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setSearchFocused(false);
  }, []);

  // Handle status filter change
  const handleChannelChange = useCallback(e => {
    setChannelFilter(e.target.value);
  }, []);

  // Handle date range change
  const handleDateRangeChange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Apply filters and persist to URL
  const applyFilters = useCallback(() => {
    const requestParams = {
      page: 1,
      size: pagination.size,
      channel: channelFilter || 'ALL',
    };

    if (searchTerm && searchTerm.length >= 3) requestParams.keyword = searchTerm;
    if (startDate && endDate) {
      requestParams.dateFrom = startDate;
      requestParams.dateTo = endDate;
    }

    setSearchParams(serializeFiltersToParamsReversal({
      page: 1,
      size: pagination.size,
      keyword: searchTerm,
      dateFrom: startDate,
      dateTo: endDate,
      channel: channelFilter,
    }), { replace: true });
    fetchTransactions(requestParams);
  }, [
    searchTerm,
    startDate,
    endDate,
    channelFilter,
    fetchTransactions,
    pagination.size,
    setSearchParams,
  ]);

  // Clear all filters and URL
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setChannelFilter('');
    setSearchParams({}, { replace: true });
    fetchTransactions({ page: 1, size: pagination.size });
  }, [fetchTransactions, pagination.size, setSearchParams]);

  // Handle page change and persist to URL
  const handlePageChange = useCallback(
    newPage => {
      const requestParams = {
        page: newPage,
        size: pagination.size,
        channel: channelFilter || 'ALL',
      };

      if (searchTerm && searchTerm.length >= 3) requestParams.keyword = searchTerm;
      if (startDate && endDate) {
        requestParams.dateFrom = startDate;
        requestParams.dateTo = endDate;
      }
      if (channelFilter) requestParams.channel = channelFilter;

      setSearchParams(serializeFiltersToParamsReversal({
        page: newPage,
        size: pagination.size,
        keyword: searchTerm,
        dateFrom: startDate,
        dateTo: endDate,
        channel: channelFilter,
      }), { replace: true });
      fetchTransactions(requestParams);
    },
    [
      searchTerm,
      startDate,
      endDate,
      channelFilter,
      fetchTransactions,
      pagination.size,
      setSearchParams,
    ]
  );

  // Format amount with . as decimal separator
  const formatAmount = amount => {
    const n = typeof amount === 'number' ? amount : Number(amount);
    if (!Number.isFinite(n)) return '0.00';
    return n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Get status badge style
  const getStatusBadgeStyle = status => {
    switch (status) {
      case 'Success':
        return 'bg-green-100 text-green-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle action - navigate to detail page (pass returnSearch so back from detail keeps filter)
  const handleViewDetail = transaction => {
    navigate(`/transactions/reversal/${transaction.id}`, {
      state: { transaction, returnSearch: location.search },
    });
  };

  const handleExportCSV = () => {
    console.log('Export to CSV');
    // TODO: Implement CSV export
  };

  return (
    <div>
      <PageHeader
        title='Reversal'
        subtitle='Manage your transactions'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Reversal' },
        ]}
      />

      {/* Search and Filter Section */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
          {/* Type Filter */}
          <div>
            <label
              htmlFor='channelFilter'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Channel
            </label>
            <select
              id='channelFilter'
              value={channelFilter}
              onChange={handleChannelChange}
              className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              <option value='ALL'>All Channel</option>
              <option value='pay-go'>1 Tap</option>
              <option value='snap-earn'>1 Snap</option>
              <option value='manual'>Manual</option>
            </select>
          </div>
          {/* Search Transaction */}
          <div className='relative lg:col-span-1'>
            <label
              htmlFor='search'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Search Transaction
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg
                  className='h-5 w-5 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              <input
                type='text'
                id='search'
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder='Search by name (min 3 chars)...'
                className={`
                  block w-full pl-10 pr-3 py-2 border rounded-md text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${searchFocused ? 'border-blue-300' : 'border-gray-300'}
                  ${
                    searchTerm.length > 0 && searchTerm.length < 3
                      ? 'border-yellow-300 bg-yellow-50'
                      : ''
                  }
                `}
              />
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <div className='absolute right-3 top-2'>
                  <div className='bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded'>
                    {3 - searchTerm.length} more
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Transaction Date Range */}
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onDateRangeChange={({ startDate: start, endDate: end }) => {
              handleDateRangeChange(start, end);
            }}
            placeholder='Select transaction date range'
            label={{
              start: 'Date From',
              end: 'Date To',
            }}
          /> 

          {/* Filter Actions */}
          <div className='flex flex-col justify-end'>
            <div className='flex gap-2'>
              <button
                onClick={clearFilters}
                className='flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500'
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                disabled={loading}
                className='flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
          <div className='flex items-center'>
            <svg
              className='w-5 h-5 text-red-400 mr-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
            <p className='text-sm text-red-700'>{error}</p>
            <button
              onClick={() => setError(null)}
              className='ml-auto text-red-400 hover:text-red-600'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
          <div>
            <h3 className='text-lg font-medium text-gray-900 flex items-center gap-2'>
              Transactions List
              {loading && (
                <svg
                  className='animate-spin h-4 w-4 text-blue-600'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              )}
            </h3>
            <p className='text-sm text-gray-500 mt-1'>
              Showing {(pagination.page - 1) * pagination.size + 1} to{' '}
              {Math.min(pagination.page * pagination.size, pagination.total)} of{' '}
              {pagination.total} transactions
            </p>
          </div>
          {/* <button
            onClick={handleExportCSV}
            className='bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            Export to CSV
          </button> */}
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>                
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Company Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Sector
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Outlet
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Channel
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Shopper Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Shopper Phone Number
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Date & Time
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Amount
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 border-l border-gray-200 z-10'>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {transactions.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan='9'
                    className='px-6 py-8 text-center text-gray-500'
                  >
                    <div className='flex flex-col items-center'>
                      <svg
                        className='w-12 h-12 text-gray-300 mb-3'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                        />
                      </svg>
                      <p className='text-lg font-medium'>
                        No transactions found
                      </p>
                      <p className='text-sm'>
                        Try adjusting your search criteria or check back later.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                transactions.map((transaction, index) => (
                  <tr
                    key={transaction.id || index}
                    className='hover:bg-gray-50 group'
                  >                    
                    <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.companyName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap'>
                      <span className='inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800'>
                        {transaction.sector}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-normal break-words max-w-xs'>
                      {transaction.outlet}
                    </td>
                    <td className='px-3 py-4 whitespace-normal break-words max-w-xs'>
                      {transaction.channel}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.shopperName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.shopperPhone}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.transactionDateTime
                        ? moment(transaction.transactionDateTime).format(
                            'D MMM YYYY HH:mm'
                          )
                        : 'N/A'}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      €{formatAmount(transaction.transactionAmount)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyle(
                          transaction.status
                        )}`}
                      >
                        {transaction.status}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-center sticky right-0 bg-white group-hover:bg-gray-50 border-l border-gray-200 z-10'>
                      <button
                        onClick={() => handleViewDetail(transaction)}
                        className='text-blue-600 hover:text-blue-900 text-sm font-medium inline-flex items-center gap-1'
                      >
                        <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination UI */}
        {pagination.pageCount > 1 && (
          <div className='px-4 md:px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
            <div className='flex-1 flex justify-between sm:hidden'>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
                className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pageCount || loading}
                className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Next
              </button>
            </div>
            <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
              <div>
                <p className='text-sm text-gray-700'>
                  Page <span className='font-medium'>{pagination.page}</span> of{' '}
                  <span className='font-medium'>{pagination.pageCount}</span>
                </p>
              </div>
              <div>
                <nav
                  className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
                  aria-label='Pagination'
                >
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1 || loading}
                    className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Previous</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {Array.from(
                    { length: Math.min(5, pagination.pageCount) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.pageCount <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.pageCount - 2) {
                        pageNum = pagination.pageCount - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === pagination.page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={
                      pagination.page >= pagination.pageCount || loading
                    }
                    className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Next</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReversalTransactionsPage;
